import React, { useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import ToastProvider from 'core/providers/ToastProvider'
import BannerProvider from 'core/providers/BannerProvider'
import AppContainer from 'core/containers/AppContainer'
import { BrowserRouter as Router } from 'react-router-dom'
import pluginManager from 'core/plugins/pluginManager'
import store, { persistor } from './store'
import Progress from 'core/components/progress/Progress'
import ThemeManager from 'core/themes/ThemeManager'
import { getErrorBoundary } from './core/components/ErrorBoundary'
import { initErrorHandling } from './bootstrap/error-handling'
import './media-queries.css'
import defaultPlugins, { getOpenCloudPlugins } from 'app/plugins'
import { loadFeatures } from 'core/containers/AppController'
import { pathStrOr } from 'utils/fp'
import axios from 'axios'
import { AppPlugins, appUrlRoot } from './constants'
import { getAppBasePath, isDevelopment, isLocalhost } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { createOpenstackRoutes } from 'core/utils/routes/openstackRoutes'
import { publicRoutes } from 'core/utils/routes/publicRoutes'
import { accountRoutes } from 'core/utils/routes/accountRoutes'
import { kubernetesRoutes } from 'core/utils/routes/kubernetesRoutes'
import { opencloudRoutes } from 'core/utils/routes/opencloudRoutes'
import GroundcoverConfig from './groundcover.config'
import Groundcover from '@groundcover/browser'
import { sanitizeGroundcoverEvent } from './utils/sanitizeGroundcoverEvent'
const getOpenCloudSession = async () => {
  try {
    const url = `https://${window.location.hostname}/sso/IDP1/Shibboleth.sso/Session`
    const response = await axios.get(url)
    return response.data
  } catch (error) {
    console.error(error)
    return null
  }
}

const appBasePath = getAppBasePath()

const App = () => {
  const [loading, setLoading] = useState(true)

  // Initialize Groundcover
  const initGroundcover = () => {
    if (isDevelopment() || isLocalhost()) return

    const groundcoverConfig = GroundcoverConfig()
    Groundcover.init({
      ...groundcoverConfig,
      options: {
        beforeSend: (event) => {
          sanitizeGroundcoverEvent(event)
          return true
        },
      },
    })
  }

  useEffect(() => {
    async function initializeApp() {
      try {
        const features = await loadFeatures()
        const productType = pathStrOr('pcd', 'experimental.product_type', features)
        const airgapped = pathStrOr(false, 'experimental.airgapped', features)
        if (!airgapped) {
          initErrorHandling()
        }

        // Register plugins
        const plugins =
          productType === 'opencloud' ? getOpenCloudPlugins(defaultPlugins) : defaultPlugins
        plugins?.forEach((plugin) => pluginManager.registerPlugin(plugin))

        // Initialize RouteManager with the appropriate routes. OpenCloud and PCD use the same routes.
        const openstackRoutes = createOpenstackRoutes(
          productType === 'opencloud' ? AppPlugins.OpenCloud : AppPlugins.OpenStack,
        )

        const routes = {
          ...publicRoutes,
          ...accountRoutes,
          ...openstackRoutes,
          ...kubernetesRoutes,
          ...opencloudRoutes,
        }

        routeManager.init(routes)

        // Determine if the user is a Racker and redirect if necessary
        if (productType === 'opencloud') {
          await checkRackerRedirect()
        }
      } catch (e) {
        console.error('Error loading app:', e)
      } finally {
        setLoading(false)
      }
    }

    initGroundcover()
    initializeApp()
  }, [])

  // Check if the user is a Racker and update the path with a /r prefix if the path
  // does not start with /r
  const checkRackerRedirect = async () => {
    const rackerBasePath = '/r'
    const opencloudSession = await getOpenCloudSession()
    if (!opencloudSession) return

    const attributes = opencloudSession.attributes || []
    const roles = attributes.find((attr) => attr.name === 'rxtrole')?.values || []
    const isRacker = roles.includes('racker')
    const currentPath = window.location.pathname

    if (isRacker && !currentPath.startsWith(rackerBasePath)) {
      const currentPathHasAppRootUrl = currentPath.includes(appUrlRoot)
      window.location.replace(
        `${window.location.origin}${rackerBasePath}${
          currentPathHasAppRootUrl ? '' : appUrlRoot
        }${currentPath}`,
      )
    }
  }

  const progressInstance = (
    <Progress renderLoadingImage={false} loading message={'Loading app...'} />
  )
  const ErrorBoundary = getErrorBoundary()
  return (
    <Router basename={appBasePath}>
      <Provider store={store}>
        <PersistGate loading={progressInstance} persistor={persistor}>
          {loading ? (
            progressInstance
          ) : (
            <ErrorBoundary>
              <ThemeManager>
                <ToastProvider>
                  <BannerProvider>
                    <AppContainer />
                  </BannerProvider>
                </ToastProvider>
              </ThemeManager>
            </ErrorBoundary>
          )}
        </PersistGate>
      </Provider>
    </Router>
  )
}

export default App
